import memoize from 'lru-memoize';
import React from 'react';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {makeEnumTypeGuard} from '../../../../libs/shared/core/src/models/api/types/enumTypes';
import {
  AnalyticsGraphMetricsEnum,
  AnalyticsHeatMapMetricsEnum,
  AnalyticsMetricsKeys,
  AnalyticsMultiGraphMetricsEnum,
  AnalyticsScalarMetricsEnum,
  AnalyticsTableMetricsEnum,
} from '../../../models/api/analyticsMetricsModel';
import {
  AnalyticsFilterTypesEnum,
  AnalyticsReportFilterOptions,
} from '../../../models/api/analyticsSavedViewModel';
import {AnalyticsComputedGraphMetricsEnum} from '../../transformers/analyticsMetricsTransformersTypes';
import {
  AnalyticsFeatureStatusesEnum,
  AnalyticsReportConfig,
  AnalyticsReportsEnum,
  ConversationResolutionStrategiesEnum,
  graphConversationStagesMetrics,
  histogramConversationStagesMetrics,
  isAnalyticsReportWithConfig,
  MetricCategoriesEnum,
  ReportConfigElement,
  ReportElementIdsEnum,
  ReportFilterOptionsEnum,
  ReportMetricConfig,
  scalarConversationStagesMetrics,
} from './reportTypes';
import {getResolutionsReportConfig} from './resolutionsReportHelpers';
import {getWorkloadReportConfig} from './workloadReportHelpers';

/*
 * Constants.
 */

// We only save filters in url that we know we can safely load.
export const FILTERS_IN_URL = [
  AnalyticsFilterTypesEnum.ACCOUNTS,
  AnalyticsFilterTypesEnum.INBOXES,
  AnalyticsFilterTypesEnum.CHANNELS,
  AnalyticsFilterTypesEnum.TAGS,
  AnalyticsFilterTypesEnum.EXCLUDE_TAGS,
  AnalyticsFilterTypesEnum.WITHOUT_TAGS,
  AnalyticsFilterTypesEnum.FULL_TAGS,
  AnalyticsFilterTypesEnum.TEAMMATES,
  AnalyticsFilterTypesEnum.TEAMS,
] as const;

export const groupByMetricsByReportMap: Partial<
  Record<AnalyticsReportsEnum, ReadonlyArray<AnalyticsMetricsKeys>>
> = {
  [AnalyticsReportsEnum.WORKLOAD]: [
    AnalyticsMultiGraphMetricsEnum.WORKLOAD_BY_INBOX_WITH_TICKETING_GRAPH,
    AnalyticsMultiGraphMetricsEnum.WORKLOAD_BY_TAG_WITH_TICKETING_GRAPH,
  ],
};

/*
 * Helpers.
 */

const pctResolvedStagesReportElement = {
  // Progress bar.
  id: ReportElementIdsEnum.OVERVIEW_STAGE_RESOLVED_PCT,
  metrics: [
    {id: AnalyticsTableMetricsEnum.STAGE_PCT_RESOLVED_SEGMENTS_TABLE, isDisplayedByDefault: true}, // Will be transformed to 'STAGE_RESOLVED_SEGMENTS_PROGRESS'.
  ],
};

const defaultOverviewMetricsWithConversationStages = [
  AnalyticsScalarMetricsEnum.NEW_SEGMENTS_COUNT,
  AnalyticsScalarMetricsEnum.STAGE_NUM_RESOLVED_SEGMENTS,
  AnalyticsScalarMetricsEnum.STAGE_AVG_RESOLUTION_TIME,
  AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME,
  AnalyticsTableMetricsEnum.REPLY_TIME_HISTOGRAM,
];

const defaultOverviewMetricsWithoutConversationStages = [
  AnalyticsScalarMetricsEnum.ACTIVE_SEGMENTS,
  AnalyticsScalarMetricsEnum.NEW_SEGMENTS_COUNT,
  AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME,
  AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS,
  AnalyticsTableMetricsEnum.REPLY_TIME_HISTOGRAM,
];

function setOverviewIsDisplayedByDefault(
  item: {id: AnalyticsScalarMetricsEnum | AnalyticsTableMetricsEnum},
  isConversationStagesEnabledForInboxes: boolean,
) {
  if (isConversationStagesEnabledForInboxes) {
    return {
      id: item.id,
      isDisplayedByDefault: defaultOverviewMetricsWithConversationStages.includes(item.id),
    };
  }

  return {
    id: item.id,
    isDisplayedByDefault: defaultOverviewMetricsWithoutConversationStages.includes(item.id),
  };
}

export interface GetAnalyticsReportConfigParams {
  reportType: AnalyticsReportsEnum;
  hasAccountsAnalyticsFeature: boolean;
  hasConversationStagesFeature: boolean;
  conversationResolutionStrategy: ConversationResolutionStrategiesEnum;
}

export const getAnalyticsReportConfig = memoize()(({
  reportType,
  hasAccountsAnalyticsFeature,
  hasConversationStagesFeature,
  conversationResolutionStrategy,
}: GetAnalyticsReportConfigParams): AnalyticsReportConfig => {
  if (!isAnalyticsReportWithConfig(reportType)) {
    return {
      title: null,
      reportElements: [],
    };
  }

  return {
    [AnalyticsReportsEnum.WORKLOAD]: getWorkloadReportConfig(conversationResolutionStrategy),
    [AnalyticsReportsEnum.RESOLUTIONS]: getResolutionsReportConfig(),
    [AnalyticsReportsEnum.OVERVIEW]: {
      title: <Trans id="analytics-title-overview">Overview</Trans>,
      featureStatus: AnalyticsFeatureStatusesEnum.LEGACY,
      reportElements: [
        {
          // Hero cards.
          id: ReportElementIdsEnum.OVERVIEW_METRICS_CARDS,
          metrics: [
            {id: AnalyticsScalarMetricsEnum.ACTIVE_SEGMENTS},
            {id: AnalyticsScalarMetricsEnum.NEW_SEGMENTS_COUNT},
            {id: AnalyticsScalarMetricsEnum.MESSAGES_SENT},
            {id: AnalyticsScalarMetricsEnum.MESSAGES_RECEIVED},
            {id: AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME},
            {id: AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME},
            {id: AnalyticsScalarMetricsEnum.AVG_HANDLE_TIME},
            // Dynamically inserting stage metrics if company has the stages feature.
            ...((hasConversationStagesFeature && scalarConversationStagesMetrics) || []),
            {id: AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS},
            {id: AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY},
          ].map((item) =>
            setOverviewIsDisplayedByDefault(
              item,
              // still show stages metrics when conversation stages are disabled
              hasConversationStagesFeature,
            ),
          ),
        },
        // Dynamically inserting the % of resolved conversations with stages metric if company has the stages feature.
        ...((hasConversationStagesFeature && [pctResolvedStagesReportElement]) || []),
        {
          // Changes over time chart.
          id: ReportElementIdsEnum.OVERVIEW_CHANGES_OVER_TIME,
          metrics: [
            {id: AnalyticsGraphMetricsEnum.MESSAGES_RECEIVED_GRAPH, isDisplayedByDefault: true},
            {id: AnalyticsGraphMetricsEnum.NUM_MESSAGES_SENT_GRAPH, isDisplayedByDefault: false},
            {id: AnalyticsGraphMetricsEnum.RESPONSE_GRAPH, isDisplayedByDefault: false},
            {id: AnalyticsGraphMetricsEnum.FIRST_RESPONSE_GRAPH, isDisplayedByDefault: false},
            {id: AnalyticsGraphMetricsEnum.HANDLE_TIME_GRAPH, isDisplayedByDefault: false},
            {id: AnalyticsGraphMetricsEnum.NEW_SEGMENTS_GRAPH, isDisplayedByDefault: false},
            // Dynamically inserting stage metrics if company has the stages feature.
            ...((hasConversationStagesFeature && graphConversationStagesMetrics) || []),
            {id: AnalyticsGraphMetricsEnum.ARCHIVED_SEGMENTS_GRAPH, isDisplayedByDefault: false},
            {id: AnalyticsGraphMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY_GRAPH, isDisplayedByDefault: false},
          ],
        },
        {
          // Reply times list and Donut.
          id: ReportElementIdsEnum.OVERVIEW_REPLY_TIMES_WRAPPER,
          metrics: [
            // Dynamically inserting stage metrics if company has the stages feature.
            ...((hasConversationStagesFeature && histogramConversationStagesMetrics) || []),
            // Non-histogram metrics are included so that they are fetched for donut sections.
            // The list of metrics in the dropdown is defined in the report itself.
            {id: AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME},
            {id: AnalyticsTableMetricsEnum.REPLY_TIME_HISTOGRAM},
            {id: AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME},
            {id: AnalyticsTableMetricsEnum.FIRST_REPLY_TIME_HISTOGRAM},
          ].map((item) =>
            setOverviewIsDisplayedByDefault(
              item,
              // still show stages metrics when conversation stages are disabled
              hasConversationStagesFeature,
            ),
          ),
        },
        {
          // Team changes list.
          id: ReportElementIdsEnum.OVERVIEW_TEAM_CHANGES_HISTOGRAM,
          metrics: [{id: AnalyticsTableMetricsEnum.TEAM_CHANGES, isDisplayedByDefault: true}],
        },
        {
          // Top tags list.
          id: ReportElementIdsEnum.OVERVIEW_TAGS_HISTOGRAM,
          metrics: [{id: AnalyticsTableMetricsEnum.TOP_TAGS, isDisplayedByDefault: true}],
        },
      ],
      options: {
        [ReportFilterOptionsEnum.BUCKET_BOUNDARIES]: {
          // Default buckets for the stage time histogram metric: < 1d, 1d - 2d, 2d - 4d, 4d - 7d, > 7d
          [AnalyticsTableMetricsEnum.STAGE_AVG_RESOLUTION_TIME_HISTO]: [
            86400000, 172800000, 345600000, 604800000,
          ],
        },
        [ReportFilterOptionsEnum.PREVIOUS_PERIOD_METRICS]: [
          AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME,
          AnalyticsScalarMetricsEnum.ACTIVE_SEGMENTS,
          AnalyticsScalarMetricsEnum.NEW_SEGMENTS_COUNT,
          AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS,

          AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY,
          AnalyticsScalarMetricsEnum.MESSAGES_SENT,
          AnalyticsScalarMetricsEnum.AVG_HANDLE_TIME,
          AnalyticsScalarMetricsEnum.MESSAGES_RECEIVED,
          AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME,

          AnalyticsGraphMetricsEnum.NUM_MESSAGES_SENT_GRAPH,
          AnalyticsGraphMetricsEnum.MESSAGES_RECEIVED_GRAPH,
          AnalyticsGraphMetricsEnum.RESPONSE_GRAPH,
          AnalyticsGraphMetricsEnum.FIRST_RESPONSE_GRAPH,
          AnalyticsGraphMetricsEnum.HANDLE_TIME_GRAPH,
          AnalyticsGraphMetricsEnum.NEW_SEGMENTS_GRAPH,
          // Dynamically inserting stage metrics if the FF is on.
          ...((hasConversationStagesFeature && graphConversationStagesMetrics.map((metric) => metric.id)) ||
            []),
          AnalyticsGraphMetricsEnum.ARCHIVED_SEGMENTS_GRAPH,
          AnalyticsGraphMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY_GRAPH,

          AnalyticsTableMetricsEnum.REPLY_TIME_HISTOGRAM,
          AnalyticsTableMetricsEnum.FIRST_REPLY_TIME_HISTOGRAM,

          AnalyticsTableMetricsEnum.TEAM_CHANGES,

          AnalyticsTableMetricsEnum.TOP_TAGS,
        ],
      },
    },

    [AnalyticsReportsEnum.TEAM_PERF]: {
      title: <Trans id="analytics-title-team_performance">Team performance</Trans>,
      reportElements: [
        {
          // Hero cards.
          id: ReportElementIdsEnum.TEAM_METRICS_CARDS,
          metrics: [
            {id: AnalyticsScalarMetricsEnum.MESSAGES_SENT, isDisplayedByDefault: true},
            {id: AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME, isDisplayedByDefault: true},
            {id: AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME, isDisplayedByDefault: true},
            {id: AnalyticsScalarMetricsEnum.AVG_TOTAL_REPLY_TIME, isDisplayedByDefault: false},
            {id: AnalyticsScalarMetricsEnum.AVG_HANDLE_TIME, isDisplayedByDefault: true},
            {id: AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY, isDisplayedByDefault: false},
            {id: AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS, isDisplayedByDefault: false},
            {id: AnalyticsScalarMetricsEnum.ACTIVE_TEAMMATES, isDisplayedByDefault: false},
          ],
        },
        {
          // Busiest times heatmap.
          id: ReportElementIdsEnum.TEAM_HEATMAP,
          metrics: [
            {
              id: AnalyticsHeatMapMetricsEnum.INBOX_WITH_TICKETING,
              isDisplayedByDefault: true,
            },
          ],
        },
        {
          // Teammates perf table.
          id: ReportElementIdsEnum.TEAM_TEAMMATES_TABLE,
          metrics: [{id: AnalyticsTableMetricsEnum.TEAM_PERFORMANCE, isDisplayedByDefault: true}],
        },
      ],
      options: {
        [ReportFilterOptionsEnum.PREVIOUS_PERIOD_METRICS]: [
          AnalyticsScalarMetricsEnum.AVG_RESPONSE_TIME,
          AnalyticsScalarMetricsEnum.AVG_FIRST_REPLY_TIME,
          AnalyticsScalarMetricsEnum.ACTIVE_TEAMMATES,
          AnalyticsScalarMetricsEnum.AVG_HANDLE_TIME,
          AnalyticsScalarMetricsEnum.MESSAGES_SENT,
          AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS_WITH_REPLY,
          AnalyticsScalarMetricsEnum.ARCHIVED_SEGMENTS,
        ],
      },
    },

    [AnalyticsReportsEnum.CONVERSATIONS]: {
      title: <Trans id="analytics-title-conversations">Conversations</Trans>,
      reportElements: [
        {
          // What happened chart.
          id: ReportElementIdsEnum.CONVERSATIONS_WHAT_HAPPENED,
          metrics: [
            {id: AnalyticsTableMetricsEnum.WHAT_HAPPENED_TABLE, isDisplayedByDefault: true}, // Will be transformed to `WHAT_HAPPENED_GRAPH`.
          ],
        },
        {
          // Conversation flows sankey.
          id: ReportElementIdsEnum.CONVERSATIONS_SANKEY,
          metrics: [
            {id: AnalyticsTableMetricsEnum.SANKEY_TABLE, isDisplayedByDefault: true}, // Will be transformed to `SANKEY_GRAPH`.
          ],
        },
        {
          // Inboxes table.
          id: ReportElementIdsEnum.CONVERSATIONS_INBOXES,
          metrics: [{id: AnalyticsTableMetricsEnum.CONVERSATIONS_INBOX_TABLE, isDisplayedByDefault: true}],
        },
      ],
      options: {
        // TODO Remove this when the BE changes the semantic for omitting this option to "no previous period metrics".
        [ReportFilterOptionsEnum.PREVIOUS_PERIOD_METRICS]: [],
      },
    },

    [AnalyticsReportsEnum.MESSAGE_TEMPLATES]: {
      title: <Trans id="analytics-title-message-templates">Message templates</Trans>,
      reportElements: [
        {
          // Progress bar.
          id: ReportElementIdsEnum.MESSAGE_TEMPLATES_PCT,
          metrics: [
            {
              id: AnalyticsScalarMetricsEnum.PCT_MESSAGES_WITH_MESSAGE_TEMPLATES,
              isDisplayedByDefault: true,
            }, // Will be transformed to 'MESSAGE_TEMPLATE_REPORT_PROGRESS'.
          ],
        },
        {
          // Message templates Table.
          id: ReportElementIdsEnum.MESSAGE_TEMPLATE_TABLE,
          metrics: [{id: AnalyticsTableMetricsEnum.MESSAGE_TEMPLATE_TABLE, isDisplayedByDefault: true}],
        },
      ],
      options: {
        // TODO Remove this when the BE changes the semantic for omitting this option to "no previous period metrics".
        [ReportFilterOptionsEnum.PREVIOUS_PERIOD_METRICS]: [],
      },
    },

    [AnalyticsReportsEnum.TAGS]: {
      title: <Trans id="analytics-title-tags">Tags</Trans>,
      reportElements: [
        {
          // Progress bar.
          id: ReportElementIdsEnum.TAGS_CONVERSATIONS_PCT,
          metrics: [
            {id: AnalyticsScalarMetricsEnum.PCT_TAGGED_CONVERSATIONS, isDisplayedByDefault: true}, // Will be transformed to 'TAG_REPORT_PROGRESS'.
          ],
        },
        {
          // Tag perf table.
          id: ReportElementIdsEnum.TAGS_PERFORMANCE_TABLE,
          metrics: [{id: AnalyticsTableMetricsEnum.TAGS_ALL_TABLE, isDisplayedByDefault: true}],
        },
        {
          // Tag changes.
          id: ReportElementIdsEnum.TAG_CHANGES_TABLE,
          metrics: [{id: AnalyticsTableMetricsEnum.TAG_CHANGES_TABLE, isDisplayedByDefault: true}],
        },
      ],
      options: {
        // TODO Remove this when the BE changes the semantic for omitting this option to "no previous period metrics".
        [ReportFilterOptionsEnum.PREVIOUS_PERIOD_METRICS]: [],
      },
    },

    [AnalyticsReportsEnum.SLA]: {
      title: <Trans id="analytics-title-sla">Service level agreements</Trans>,
      reportElements: [
        {
          // Hero cards.
          id: ReportElementIdsEnum.SLA_METRICS_CARDS,
          metrics: [
            {id: AnalyticsScalarMetricsEnum.PCT_SLA_ACHIEVED, isDisplayedByDefault: true},
            {id: AnalyticsScalarMetricsEnum.NUM_SLA_TRIGGER, isDisplayedByDefault: true},
            {id: AnalyticsScalarMetricsEnum.SLA_BREACHES, isDisplayedByDefault: true},
            {id: AnalyticsScalarMetricsEnum.SLA_WARNINGS, isDisplayedByDefault: false},
            {id: AnalyticsScalarMetricsEnum.AVG_SLA_BREACH_TIME, isDisplayedByDefault: true},
          ],
        },
        {
          // SLA Perf graph.
          id: ReportElementIdsEnum.SLA_PERF_GRAPH_V2,
          metrics: [
            {id: AnalyticsGraphMetricsEnum.PCT_SLA_ACHIEVED_GRAPH, isDisplayedByDefault: true},
            {id: AnalyticsGraphMetricsEnum.NUM_SLA_BREACH_GRAPH, isDisplayedByDefault: false},
            {id: AnalyticsGraphMetricsEnum.NUM_SLA_WARNING_GRAPH, isDisplayedByDefault: false},
          ],
        },
        {
          // SLA Table V2.
          id: ReportElementIdsEnum.SLA_STATUS_TABLE_V2,
          metrics: [{id: AnalyticsTableMetricsEnum.SLA_STATUS_TABLE_V2, isDisplayedByDefault: true}],
        },
      ],
      options: {
        [ReportFilterOptionsEnum.PREVIOUS_PERIOD_METRICS]: [
          AnalyticsScalarMetricsEnum.PCT_SLA_ACHIEVED,
          AnalyticsScalarMetricsEnum.SLA_BREACHES,
          AnalyticsScalarMetricsEnum.SLA_WARNINGS,
          AnalyticsScalarMetricsEnum.AVG_SLA_BREACH_TIME,

          AnalyticsGraphMetricsEnum.PCT_SLA_ACHIEVED_GRAPH,
          AnalyticsGraphMetricsEnum.NUM_SLA_BREACH_GRAPH,
          AnalyticsGraphMetricsEnum.NUM_SLA_WARNING_GRAPH,
        ],
      },
    },

    [AnalyticsReportsEnum.CUSTOMER_EXPERIENCE]: {
      title: <Trans id="analytics-title-customer_experience">Customer satisfaction</Trans>,
      reportElements: [
        {
          // CX Total Survey Count Scalar.
          id: ReportElementIdsEnum.CX_TOTAL_SCALAR,
          metrics: [{id: AnalyticsScalarMetricsEnum.NUM_CSAT_SURVEY_RESPONSE, isDisplayedByDefault: true}],
        },
        {
          // CX Graph.
          id: ReportElementIdsEnum.CX_CHANGES_OVER_TIME_CHART,
          metrics: [
            {id: AnalyticsGraphMetricsEnum.CSAT_SURVEY_SATISFACTION_GRAPH, isDisplayedByDefault: true},
            {id: AnalyticsGraphMetricsEnum.CSAT_SURVEY_RESPONSE_GRAPH, isDisplayedByDefault: false},
          ],
        },
        {
          // Accounts Table.
          id: ReportElementIdsEnum.CX_ACCOUNTS_TABLE,
          metrics: [
            {id: AnalyticsTableMetricsEnum.ACCOUNTS_TABLE, isDisplayedByDefault: hasAccountsAnalyticsFeature},
          ],
        },
        {
          // CX Table V2.
          id: ReportElementIdsEnum.CX_CUSTOMERS_TABLE_V2,
          metrics: [
            {
              id: AnalyticsTableMetricsEnum.CUSTOMERS_TABLE_V2,
              isDisplayedByDefault: !hasAccountsAnalyticsFeature,
            },
          ],
        },
        {
          // Score summary Donut chart
          id: ReportElementIdsEnum.CX_SCORES_DONUT_WRAPPER,
          metrics: [{id: AnalyticsTableMetricsEnum.CSAT_SURVEY_RESPONSE_HISTO, isDisplayedByDefault: true}],
        },
        // Donut report list.
        {
          id: ReportElementIdsEnum.CX_SCORES_LIST_WRAPPER,
          metrics: [{id: AnalyticsTableMetricsEnum.CSAT_SURVEY_RESPONSE_HISTO, isDisplayedByDefault: true}],
        },
        {
          // Score summary Donut chart
          id: ReportElementIdsEnum.CX_SCORES_AVG,
          metrics: [
            {id: AnalyticsScalarMetricsEnum.PCT_CSAT_SURVEY_SATISFACTION, isDisplayedByDefault: true},
          ],
        },
        {
          // CX Sample Feedback Table.
          id: ReportElementIdsEnum.CX_SAMPLE_FEEDBACK_TABLE,
          metrics: [
            {id: AnalyticsTableMetricsEnum.CSAT_SURVEY_SAMPLE_FEEDBACK_TABLE, isDisplayedByDefault: true},
          ],
        },
      ],
      options: {
        [ReportFilterOptionsEnum.ALWAYS_EMIT_CONTACT_ID]: true,
        // TODO Remove this when the BE changes the semantic for omitting this option to "no previous period metrics".
        [ReportFilterOptionsEnum.PREVIOUS_PERIOD_METRICS]: [],
      },
    },
    [AnalyticsReportsEnum.KNOWLEDGE_BASE]: {
      title: <Trans id="analytics-title-knowledge_base">Knowledge base</Trans>,
      reportElements: [
        {
          // Hero cards.
          id: ReportElementIdsEnum.KNOWLEDGE_BASE_METRIC_CARDS,
          metrics: [
            {
              id: AnalyticsScalarMetricsEnum.OVERVIEW_NUM_TOTAL_VIEWS,
              isDisplayedByDefault: true,
              isDisabled: false,
              category: MetricCategoriesEnum.VOLUME,
            },
            {
              id: AnalyticsScalarMetricsEnum.OVERVIEW_NUM_TOTAL_UNIQUE_VIEWS,
              isDisplayedByDefault: true,
              isDisabled: false,
              category: MetricCategoriesEnum.VOLUME,
            },
            {
              id: AnalyticsScalarMetricsEnum.OVERVIEW_NUM_TOTAL_ARTICLE_VIEWS,
              isDisplayedByDefault: true,
              isDisabled: false,
              category: MetricCategoriesEnum.VOLUME,
            },
            {
              id: AnalyticsScalarMetricsEnum.OVERVIEW_NUM_ARTICLE_UPDATES,
              isDisplayedByDefault: false,
              isDisabled: false,
              category: MetricCategoriesEnum.VOLUME,
            },
          ],
        },
      ],
    },
  }[reportType];
});

export interface GetReportBaseOptionsParams {
  reportType: AnalyticsReportsEnum;
  metrics: ReadonlyArray<string>;
  hasAccountsAnalyticsFeature: boolean;
  hasConversationStagesFeature: boolean;
  conversationResolutionStrategy: ConversationResolutionStrategiesEnum;
}

export function getReportBaseOptions({
  reportType,
  metrics,
  hasAccountsAnalyticsFeature,
  hasConversationStagesFeature,
  conversationResolutionStrategy,
}: GetReportBaseOptionsParams): AnalyticsReportFilterOptions | undefined {
  const {options} = getAnalyticsReportConfig({
    reportType,
    hasAccountsAnalyticsFeature,
    hasConversationStagesFeature,
    conversationResolutionStrategy,
  });

  // Make sure we don't ask previous period options for metrics we don't compute.
  const sanitizedOptions = options?.previous_period_metrics
    ? {
        ...options,
        previous_period_metrics: options.previous_period_metrics.filter((previousPeriodMetric) =>
          metrics.includes(previousPeriodMetric),
        ),
      }
    : options;

  return sanitizedOptions ?? {};
}

/**
 * For a given report element, returns the list of available metrics and metrics displayed by default.
 */
export interface GetReportElementMetricsParams {
  reportType: AnalyticsReportsEnum;
  reportSectionId: ReportElementIdsEnum;
  hasAccountsAnalyticsFeature: boolean;
  hasConversationStagesFeature: boolean;
  conversationResolutionStrategy: ConversationResolutionStrategiesEnum;
}

export function getReportElementMetrics<T extends AnalyticsMetricsKeys>({
  reportType,
  reportSectionId,
  hasAccountsAnalyticsFeature,
  hasConversationStagesFeature,
  conversationResolutionStrategy,
}: GetReportElementMetricsParams): {
  displayedMetrics: Array<T>;
  disabledMetrics: Array<T>;
  availableMetrics: Array<T>;
  metricsByCategory: Partial<Record<MetricCategoriesEnum, Array<ReportMetricConfig<T>>>>;
} {
  const reportElementConfig = getAnalyticsReportConfig({
    reportType,
    hasAccountsAnalyticsFeature,
    hasConversationStagesFeature,
    conversationResolutionStrategy,
  }).reportElements.find((element): element is ReportConfigElement<T> => element.id === reportSectionId);

  if (!reportElementConfig) {
    return {displayedMetrics: [], availableMetrics: [], disabledMetrics: [], metricsByCategory: {}};
  }

  return getMetricsFromReportElementConfig<T>(reportElementConfig);
}

export function getMetricsFromReportElementConfig<T extends AnalyticsMetricsKeys>(
  reportElementConfig: ReportConfigElement<T>,
) {
  const {metrics = [], columns} = reportElementConfig;

  const displayedMetrics: Array<T> = [];
  const disabledMetrics: Array<T> = [];
  const availableMetrics: Array<T> = [];
  const metricsByCategory: Record<MetricCategoriesEnum, Array<ReportMetricConfig<T>>> = {
    [MetricCategoriesEnum.VOLUME]: [],
    [MetricCategoriesEnum.EFFICIENCY]: [],
    [MetricCategoriesEnum.WORKFORCE]: [],
  };

  for (const metricConfig of metrics) {
    if (metricConfig.isDisplayedByDefault) {
      displayedMetrics.push(metricConfig.id);
    }

    if (metricConfig.isDisabled) {
      disabledMetrics.push(metricConfig.id);
    }

    availableMetrics.push(metricConfig.id);

    if (metricConfig.category && !columns) {
      metricsByCategory[metricConfig.category].push(metricConfig);
    }
  }

  // For table metrics, consider the columns as the metrics to be displayed in the metric selection dropdown.
  if (columns) {
    for (const column of columns) {
      if (column.category) {
        metricsByCategory[column.category].push(column);
      }
    }
  }

  return {
    displayedMetrics,
    availableMetrics,
    disabledMetrics,
    metricsByCategory,
  };
}

/*
 * Type guards.
 */

export const isGraphMetric = makeEnumTypeGuard(AnalyticsGraphMetricsEnum);
export const isScalarMetric = makeEnumTypeGuard(AnalyticsScalarMetricsEnum);
export const isTableMetric = makeEnumTypeGuard(AnalyticsTableMetricsEnum);
export const isComputedGraphMetric = makeEnumTypeGuard(AnalyticsComputedGraphMetricsEnum);

/*
 * Const.
 */

export const segmentsHelperCenterUrl = 'https://help.front.com/en/articles/2129';
export const ticketStatusHelpCenterUrl = 'https://help.front.com/en/articles/1300288';
export const conversationStagesHelpCenterUrl = 'https://help.front.com/en/articles/2556';
export const conversationStagesMetricsGlossaryHelpCenterUrl = 'https://help.front.com/en/articles/2557';
export const workloadReportHelpCenterUrl = `https://help.front.com/en/articles/911360`;
// TODO Update once the help center article is created.
export const resolutionsReportHelpCenterUrl = `https://help.front.com/en/articles/todo`;

export const reportSectionBaseHeight = 300;
